import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { store, RootState } from './store/index';
import { mqttService } from './mqttService';
import { NotificationProvider } from './utils/notification';
import { setAppNavigate } from './utils/navigation';
import { buildAntdTheme } from './utils/theme';

import './styles/style.scss';
import './styles/board.scss';

import Layout from './Layout';
import TodoDetailsPage from './pages/TodoDetailsPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectOverviewPage from './pages/ProjectOverviewPage';
import IndexPage from './pages/IndexPage';
import ErrorPage from './pages/ErrorPage';

function loadFont(fontName: string) {
    const formattedFontName = fontName.replace(/\s+/g, '+');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${formattedFontName}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    document.body.style.fontFamily = `'${fontName}', sans-serif`;
}

const StoreProvider = () => {
    return (
        <Provider store={store}>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </Provider>
    );
};

const App = () => {
    const settings = useSelector((state: RootState) => state.settings);
    const location = useLocation();
    const navigate = useNavigate();
    const isIndexPage = location.pathname === '/';
    const theme = settings.theme;

    useEffect(() => {
        setAppNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        mqttService.init();
        return () => {
            mqttService.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!isIndexPage && theme) {
            loadFont(theme.fontFamily);
        }
    }, [isIndexPage, theme]);

    const antdThemeConfig = useMemo(
        () => (isIndexPage ? { cssVar: { key: 'vdelo' as const } } : buildAntdTheme(theme)),
        [isIndexPage, theme]
    );

    return (
        <ConfigProvider
            locale={ruRU}
            theme={antdThemeConfig}
            // Модалки/дропдауны в portal должны видеть CSS-переменные темы
            getPopupContainer={() =>
                (document.querySelector('.css-var-vdelo') as HTMLElement) || document.body
            }
        >
            <NotificationProvider>
                <Routes>
                    <Route index element={<IndexPage />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/projects" element={<Layout />}>
                        <Route index element={<ProjectsPage />} />
                        <Route path=":projectUrl" element={<ProjectOverviewPage />} />
                        <Route path=":projectUrl/:todoId" element={<TodoDetailsPage />} />
                    </Route>
                </Routes>
            </NotificationProvider>
        </ConfigProvider>
    );
};

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

root.render(
    <React.StrictMode>
        <StoreProvider />
    </React.StrictMode>
);
